import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { getServerUrl } from '../config';

/**
 * Get the user's payment history and subscription information
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - Object containing payment history and subscription info
 */
export const getUserPaymentHistory = async (userId) => {
  try {
    // Get the correct Stripe server URL from config
    const baseUrl = getServerUrl('stripe');
    
    // Create the API URL, ensuring no port is included in production
    let apiUrl;
    if (process.env.NODE_ENV === 'production') {
      // In production, use the domain without port
      const url = new URL(baseUrl);
      // Force HTTPS protocol
      url.protocol = 'https:';
      // Remove port if present
      url.port = '';
      apiUrl = `${url.origin}/api/payment-history/${userId}`;
    } else {
      // In development, use the full URL with port
      apiUrl = `${baseUrl}/api/payment-history/${userId}`;
    }
    
    console.log(`Using Stripe API URL: ${apiUrl}`);
    
    // Set a timeout for the fetch request
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      
      // Clear the timeout
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch payment history from server: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Error retrieving payment history');
      }
      
      console.log('Payment history from server:', data);
      console.log('Subscription data from server:', data.subscription);
      
      // If we have a subscription with a type from the server, verify the plan name
      if (data.subscription && data.subscription.type) {
        console.log(`Client: Verifying plan name for type: ${data.subscription.type}`);
        try {
          const planDoc = await getDoc(doc(db, 'subscriptionPlans', data.subscription.type));
          if (planDoc.exists()) {
            const planName = planDoc.data().name;
            console.log(`Client: Found plan name in Firestore: ${planName}`);
            console.log(`Client: Server provided plan name: ${data.subscription.planName}`);
            
            // Always update the plan name from Firestore to ensure it's correct
            console.log(`Client: Updating plan name from ${data.subscription.planName} to ${planName}`);
            data.subscription.planName = planName;
          } else {
            console.log(`Client: Plan not found in Firestore for type: ${data.subscription.type}`);
            // Fallback to hardcoded names if Firestore lookup fails
            if (data.subscription.type === 'plan_1') data.subscription.planName = 'Basic';
            if (data.subscription.type === 'plan_2') data.subscription.planName = 'Standard';
            if (data.subscription.type === 'plan_3') data.subscription.planName = 'Premium';
            if (data.subscription.type === 'plan_4') data.subscription.planName = 'Enterprise';
            if (data.subscription.type === 'plan_5') data.subscription.planName = 'Ultimate';
          }
        } catch (error) {
          console.error('Error verifying plan name:', error);
          // Fallback to hardcoded names if there's an error
          if (data.subscription.type === 'plan_1') data.subscription.planName = 'Basic';
          if (data.subscription.type === 'plan_2') data.subscription.planName = 'Standard';
          if (data.subscription.type === 'plan_3') data.subscription.planName = 'Premium';
          if (data.subscription.type === 'plan_4') data.subscription.planName = 'Enterprise';
          if (data.subscription.type === 'plan_5') data.subscription.planName = 'Ultimate';
        }
      }
      
      return {
        payments: data.payments || [],
        subscription: data.subscription
      };
    } catch (fetchError) {
      // Clear the timeout if it hasn't triggered yet
      clearTimeout(timeoutId);
      
      // Log the specific fetch error
      console.error('Fetch error:', fetchError);
      
      // Re-throw to be caught by the outer try-catch
      throw fetchError;
    }
  } catch (error) {
    console.error('Error getting payment history from server:', error);
    console.log('Falling back to Firestore for payment history');
    
    // Always fall back to Firestore data for production issues
    try {
      console.log('Attempting to get payment history from Firestore');
      return await getPaymentHistoryFromFirestore(userId);
    } catch (firestoreError) {
      console.error('Error getting payment history from Firestore:', firestoreError);
      // Return empty data if both server and Firestore fail
      return { payments: [], subscription: null };
    }
  }
};

/**
 * Fallback function to get payment history directly from Firestore
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} - Object containing payment history and subscription info
 */
async function getPaymentHistoryFromFirestore(userId) {
  try {
    // Get user document to check subscription info
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userDoc.data();
    let subscriptionInfo = null;
    let payments = [];
    
    // Process subscription information if it exists
    if (userData.subscription && userData.subscription.stripeSubscriptionId) {
      const { subscription } = userData;
      
      // Get plan name from subscription type
      let planName = 'Unknown Plan';
      
      // Try to get the plan name from Firestore
      if (subscription.type) {
        try {
          console.log(`Fetching plan name for type: ${subscription.type}`);
          const planDoc = await getDoc(doc(db, 'subscriptionPlans', subscription.type));
          console.log(`Plan document exists: ${planDoc.exists()}`);
          if (planDoc.exists()) {
            planName = planDoc.data().name || 'Unknown Plan';
            console.log(`Found plan name in Firestore: ${planName}`);
          } else {
            console.log(`Plan document not found for ${subscription.type}`);
          }
        } catch (error) {
          console.error('Error fetching plan name from Firestore:', error);
          // Fallback to hardcoded names only if Firestore fetch fails
          if (subscription.type === 'plan_1') planName = 'Basic';
          if (subscription.type === 'plan_2') planName = 'Standard';
          if (subscription.type === 'plan_3') planName = 'Premium';
          if (subscription.type === 'plan_4') planName = 'Enterprise';
          if (subscription.type === 'plan_5') planName = 'Ultimate';
          console.log(`Using fallback plan name: ${planName}`);
        }
      } else {
        console.log('No subscription type available');
      }
      
      // Determine billing period
      const billingPeriod = subscription.billingPeriod || 'month';
      
      subscriptionInfo = {
        planName: subscription.planName || planName,
        status: subscription.status || 'inactive',
        price: subscription.price || 0,
        billingPeriod: billingPeriod,
        nextBillingDate: subscription.currentPeriodEnd || null,
        startDate: subscription.currentPeriodStart || null,
        stripeSubscriptionId: subscription.stripeSubscriptionId || null,
        cancelAtPeriodEnd: subscription.cancelAtPeriodEnd || false
      };
      
      // Add subscription payment to history if active
      if (subscription.status === 'active' && subscription.currentPeriodStart) {
        payments.push({
          id: `subscription-${subscription.stripeSubscriptionId || Date.now()}`,
          date: subscription.currentPeriodStart,
          amount: subscription.price,
          description: `${subscriptionInfo.planName} Subscription (${billingPeriod})`,
          status: 'paid',
          type: 'subscription',
          billingPeriod: billingPeriod,
          invoiceNumber: subscription.lastInvoiceNumber || null,
          invoiceUrl: subscription.lastInvoiceUrl || null
        });
      }
    }
    
    // Get flex credit purchases
    if (userData.creditPurchases && Array.isArray(userData.creditPurchases)) {
      userData.creditPurchases.forEach(purchase => {
        payments.push({
          id: `credit-${purchase.purchasedAt || Date.now()}`,
          date: purchase.purchasedAt,
          amount: purchase.amount,
          description: `${purchase.credits} Flex Credits`,
          status: 'paid',
          type: 'credit',
          invoiceNumber: purchase.invoiceNumber || null,
          invoiceUrl: purchase.invoiceUrl || null,
          invoicePdf: purchase.invoicePdf || null,
          receiptUrl: purchase.receiptUrl || null
        });
      });
    }
    
    // Sort payments by date (newest first)
    payments.sort((a, b) => {
      const dateA = a.date instanceof Date ? a.date : new Date(a.date);
      const dateB = b.date instanceof Date ? b.date : new Date(b.date);
      return dateB - dateA;
    });
    
    return { payments, subscription: subscriptionInfo };
  } catch (error) {
    console.error('Error getting payment history from Firestore:', error);
    return { payments: [], subscription: null };
  }
}

/**
 * Test connectivity to the Stripe server
 * @returns {Promise<Object>} - Object containing test results
 */
export const testStripeServerConnectivity = async () => {
  try {
    // Get the correct Stripe server URL from config
    const baseUrl = getServerUrl('stripe');
    
    // Create the API URL, ensuring no port is included in production
    let apiUrl;
    if (process.env.NODE_ENV === 'production') {
      // In production, use the domain without port
      const url = new URL(baseUrl);
      // Force HTTPS protocol
      url.protocol = 'https:';
      // Remove port if present
      url.port = '';
      apiUrl = `${url.origin}/cors-test`;
    } else {
      // In development, use the full URL with port
      apiUrl = `${baseUrl}/cors-test`;
    }
    
    console.log(`Testing Stripe server connectivity at: ${apiUrl}`);
    
    // Set a timeout for the fetch request
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      
      // Clear the timeout
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        return {
          success: false,
          error: `Server responded with status: ${response.status}`,
          status: response.status,
          statusText: response.statusText
        };
      }
      
      const data = await response.json();
      return {
        success: true,
        data,
        message: 'Successfully connected to Stripe server'
      };
    } catch (fetchError) {
      // Clear the timeout if it hasn't triggered yet
      clearTimeout(timeoutId);
      
      return {
        success: false,
        error: fetchError.message,
        type: 'fetch_error',
        name: fetchError.name
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error.message,
      type: 'general_error'
    };
  }
};
